/**
 * Client-side crypto wrapper: ties the @shared/crypto primitives to the
 * SPA's unlock flow. Handles KEK derivation, ASK unwrap, ECDH priv
 * unwrap, metadata key unwrap, server verifier (master_password_hash)
 * computation.
 *
 * Unlocked state is held in-memory only; never persisted to localStorage.
 */

import { deriveKek, normalizePassword } from "@shared/crypto/argon2.js";
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  aesKwUnwrap,
  aesKwWrap,
  buildVaultItemAad,
  generateAesKey,
  generateIv,
  importAesGcmKey,
  importAesKwKey,
} from "@shared/crypto/aes.js";
import { fromBase64, toBase64 } from "@shared/crypto/bytes.js";
import { unwrapCkAsRecipient, wrapCkForRecipient, generateEcdhKeypair } from "@shared/crypto/ecdh.js";
import { generateSigningKeypair } from "@shared/crypto/signing.js";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 as nobleSha256 } from "@noble/hashes/sha256";
import {
  asBrand,
  type CiphertextBytes,
  type EcdhPrivJwk,
  type EcdhPubJwk,
  type Ed25519PubJwk,
  type IvBytes,
  type KdfSaltBytes,
  type RawSymmetricKey,
  type Sha256Bytes,
  type VaultItemAadContext,
  type VaultItemId,
  type VaultScope,
  type WrappedKeyBytes,
  DEFAULT_ARGON2ID_PARAMS,
} from "@shared/types/index.js";

const MPH_INFO = new TextEncoder().encode("ovault-server-verifier-v1");

export interface UnlockedState {
  kek: RawSymmetricKey;
  ask: RawSymmetricKey;
  ecdhPrivJwk: EcdhPrivJwk;
  ecdhPubJwk: EcdhPubJwk;
  metadataKey: RawSymmetricKey;
  signingPubJwk: Ed25519PubJwk | null;
}

/**
 * Compute the server-side master password verifier from KEK + salt.
 * MUST match the server-side compare in /api/auth/login.
 */
export function computeMasterPasswordHash(args: {
  kek: RawSymmetricKey;
  kdfSalt: Uint8Array;
}): Sha256Bytes {
  const out = hkdf(nobleSha256, args.kek, args.kdfSalt, MPH_INFO, 32);
  return asBrand<Sha256Bytes>(out);
}

/**
 * Persist unlocked key material in sessionStorage so a page reload does
 * not force the user to re-enter the master password. sessionStorage is
 * scoped to the current tab and cleared when the tab closes — a sensible
 * trade-off for a corporate vault where the OS device lock + saved-
 * password manager already enforce the human/device boundary. NOT placed
 * in localStorage to avoid persisting across browser restarts.
 *
 * Serialisation: branded Uint8Array fields → base64. JWK strings stored
 * as-is.
 */

const UNLOCK_STORAGE_KEY = "ovault_unlocked_v1";

interface PersistedUnlock {
  kek_b64: string;
  ask_b64: string;
  ecdhPrivJwk: string;
  ecdhPubJwk: string;
  metadataKey_b64: string;
  signingPubJwk: string | null;
}

// localStorage so the unlocked state survives tab close + browser
// restart on devices the user has explicitly approved via the email
// magic-link. Tradeoff: more exposure to XSS than sessionStorage, but
// matches user expectation ("on approved devices, never ask the master
// password again") and what 1Password/Bitwarden offer with their
// "Stay signed in" toggle. Wiped by clearUnlocked() on logout.
export function persistUnlocked(u: UnlockedState): void {
  const payload: PersistedUnlock = {
    kek_b64: toBase64(u.kek),
    ask_b64: toBase64(u.ask),
    ecdhPrivJwk: u.ecdhPrivJwk,
    ecdhPubJwk: u.ecdhPubJwk,
    metadataKey_b64: toBase64(u.metadataKey),
    signingPubJwk: u.signingPubJwk,
  };
  try {
    localStorage.setItem(UNLOCK_STORAGE_KEY, JSON.stringify(payload));
    // Migration: drop any stale sessionStorage copy from prior versions.
    sessionStorage.removeItem(UNLOCK_STORAGE_KEY);
  } catch {
    // best-effort: private mode may throw
  }
}

export function restoreUnlocked(): UnlockedState | null {
  try {
    // Prefer localStorage; fall back to sessionStorage for sessions that
    // were started before the storage backend was changed.
    const raw = localStorage.getItem(UNLOCK_STORAGE_KEY) ?? sessionStorage.getItem(UNLOCK_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedUnlock;
    return {
      kek: asBrand<RawSymmetricKey>(fromBase64(p.kek_b64)),
      ask: asBrand<RawSymmetricKey>(fromBase64(p.ask_b64)),
      ecdhPrivJwk: p.ecdhPrivJwk as EcdhPrivJwk,
      ecdhPubJwk: p.ecdhPubJwk as EcdhPubJwk,
      metadataKey: asBrand<RawSymmetricKey>(fromBase64(p.metadataKey_b64)),
      signingPubJwk: (p.signingPubJwk as Ed25519PubJwk | null) ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Re-wrap the current ASK under a fresh KEK derived from a new password.
 * Returns everything the /api/auth/change-password endpoint needs.
 *
 * ECDH priv, metadata key, signing priv stay the same (wrapped under ASK).
 * No vault item re-encryption required.
 */
export async function rewrapAskForPasswordChange(args: {
  currentUnlocked: UnlockedState;
  newPassword: string;
}): Promise<{
  newKdfSalt: Uint8Array;
  newKdfParamsJson: string;
  newWrappedAskB64: string;
  newMasterPasswordHashB64: string;
}> {
  const newKdfSalt = crypto.getRandomValues(new Uint8Array(16));
  const params = DEFAULT_ARGON2ID_PARAMS;
  const newKek = await deriveKek({
    password: normalizePassword(args.newPassword),
    salt: asBrand<KdfSaltBytes>(newKdfSalt),
    params,
  });
  // wrapped_ask format: 12-byte zero envelope marker || AES-KW(KEK, ASK)
  const newKekKw = await importAesKwKey(newKek);
  const askWrappedRaw = await aesKwWrap(newKekKw, args.currentUnlocked.ask);
  const padded = new Uint8Array(12 + askWrappedRaw.length);
  padded.set(askWrappedRaw, 12);
  const newMph = hkdf(nobleSha256, newKek, newKdfSalt, MPH_INFO, 32);
  return {
    newKdfSalt,
    newKdfParamsJson: JSON.stringify(params),
    newWrappedAskB64: toBase64(padded),
    newMasterPasswordHashB64: toBase64(newMph),
  };
}

/**
 * Wipe all unlocked key material: both sessionStorage AND the
 * in-memory window.__unlocked reference. Without this, the KEK/ASK
 * remained reachable from JS until GC after logout / password change —
 * an XSS-style code-injection attack window.
 */
export function clearUnlocked(): void {
  try {
    localStorage.removeItem(UNLOCK_STORAGE_KEY);
    sessionStorage.removeItem(UNLOCK_STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    delete (window as unknown as { __unlocked?: unknown }).__unlocked;
  } catch {
    (window as unknown as { __unlocked?: unknown }).__unlocked = undefined;
  }
}

export interface DeriveKekArgs {
  password: string;
  kdfSalt: Uint8Array;
  kdfParams: { algo: "argon2id"; m: number; t: number; p: number; hashLength: number };
}

export async function deriveKekForLogin(args: DeriveKekArgs): Promise<RawSymmetricKey> {
  const out = await deriveKek({
    password: normalizePassword(args.password),
    salt: asBrand<KdfSaltBytes>(args.kdfSalt),
    params: args.kdfParams,
  });
  return out;
}

/**
 * Unwrap the full key hierarchy after login. Server returns wrapped material;
 * we unwrap locally and keep raw keys in memory.
 */
export interface UnlockArgs {
  kek: RawSymmetricKey;
  wrapped_ask_b64: string;
  wrapped_priv_b64: string;
  wrapped_metadata_key_b64: string;
  ecdh_pub_jwk: string;
  signing_pub_jwk: string | null;
}

export async function unlockLocal(args: UnlockArgs): Promise<UnlockedState> {
  const wrappedAsk = fromBase64(args.wrapped_ask_b64);
  // wrapped_ask format: 12-byte zero envelope marker + AES-KW(KEK, ASK)
  const askMarker = wrappedAsk.slice(0, 12);
  for (let i = 0; i < 12; i++) {
    if (askMarker[i] !== 0) throw new Error("wrapped_ask envelope marker is non-zero");
  }
  const askWrapped = wrappedAsk.slice(12);
  const kekKw = await importAesKwKey(args.kek);
  const askRaw = await aesKwUnwrap(kekKw, asBrand<WrappedKeyBytes>(askWrapped));

  const ask = asBrand<RawSymmetricKey>(askRaw);
  const askGcm = await importAesGcmKey(ask);

  // wrapped_priv format: 12B IV + AES-GCM(ASK, utf8(ecdh_priv_jwk))
  const wp = fromBase64(args.wrapped_priv_b64);
  const wpIv = asBrand<IvBytes>(wp.slice(0, 12));
  const wpCt = asBrand<CiphertextBytes>(wp.slice(12));
  const privJwkBytes = await aesGcmDecrypt({ key: askGcm, iv: wpIv, ciphertext: wpCt });
  const ecdhPrivJwk = new TextDecoder().decode(privJwkBytes) as EcdhPrivJwk;

  // wrapped_metadata_key format: 12B IV + AES-GCM(ASK, raw 32B metadata_key)
  const wm = fromBase64(args.wrapped_metadata_key_b64);
  const wmIv = asBrand<IvBytes>(wm.slice(0, 12));
  const wmCt = asBrand<CiphertextBytes>(wm.slice(12));
  const metaRaw = await aesGcmDecrypt({ key: askGcm, iv: wmIv, ciphertext: wmCt });

  return {
    kek: args.kek,
    ask,
    ecdhPrivJwk,
    ecdhPubJwk: args.ecdh_pub_jwk as EcdhPubJwk,
    metadataKey: asBrand<RawSymmetricKey>(metaRaw),
    signingPubJwk: (args.signing_pub_jwk as Ed25519PubJwk | null) ?? null,
  };
}

// =============================================================================
// Item encrypt / decrypt
// =============================================================================

export interface DecryptItemArgs {
  item_id: string;
  scope: "personal" | "company";
  version: number;
  ciphertext_b64: string;
  iv_b64: string;
  name_ct_b64: string;
  name_iv_b64: string;
  ephemeral_pub_jwk: string;
  wrapped_ck_b64: string;
  unlocked: UnlockedState;
}

export interface DecryptedItem {
  name: string;
  value: unknown;
}

export async function decryptItem(args: DecryptItemArgs): Promise<DecryptedItem> {
  // Unwrap CK using our ECDH priv
  const ck = await unwrapCkAsRecipient(
    asBrand<WrappedKeyBytes>(fromBase64(args.wrapped_ck_b64)),
    args.ephemeral_pub_jwk as EcdhPubJwk,
    args.unlocked.ecdhPrivJwk,
  );
  const ckGcm = await importAesGcmKey(ck);

  const aadCtx: VaultItemAadContext = {
    item_id: args.item_id as VaultItemId,
    version: args.version,
    scope: args.scope as VaultScope,
  };
  const valueBytes = await aesGcmDecrypt({
    key: ckGcm,
    iv: asBrand<IvBytes>(fromBase64(args.iv_b64)),
    ciphertext: asBrand<CiphertextBytes>(fromBase64(args.ciphertext_b64)),
    aad: buildVaultItemAad(aadCtx),
  });
  const valueText = new TextDecoder().decode(valueBytes);
  let value: unknown;
  try { value = JSON.parse(valueText); } catch { value = { raw: valueText }; }

  // Decrypt name with metadata key. AAD = utf8(item_id + ':name')
  const metaGcm = await importAesGcmKey(args.unlocked.metadataKey);
  const nameAad = new TextEncoder().encode(`${args.item_id}:name`);
  const nameBytes = await aesGcmDecrypt({
    key: metaGcm,
    iv: asBrand<IvBytes>(fromBase64(args.name_iv_b64)),
    ciphertext: asBrand<CiphertextBytes>(fromBase64(args.name_ct_b64)),
    aad: nameAad,
  });
  return { name: new TextDecoder().decode(nameBytes), value };
}

export interface EncryptItemArgs {
  item_id: string;
  scope: "personal" | "company";
  version: number;
  name: string;
  value: unknown;
  recipients: Array<{ user_id: string; ecdh_pub_jwk: string }>;
  unlocked: UnlockedState;
}

export interface EncryptedItem {
  name_ct_b64: string;
  name_iv_b64: string;
  ciphertext_b64: string;
  iv_b64: string;
  wraps: Array<{ recipient_id: string; ephemeral_pub_jwk: string; wrapped_ck_b64: string }>;
}

export async function encryptItem(args: EncryptItemArgs): Promise<EncryptedItem> {
  // Fresh CK
  const ck = generateAesKey();
  const ckGcm = await importAesGcmKey(ck);
  const valueIv = generateIv();
  const aadCtx: VaultItemAadContext = {
    item_id: args.item_id as VaultItemId,
    version: args.version,
    scope: args.scope as VaultScope,
  };
  const ciphertext = await aesGcmEncrypt({
    key: ckGcm,
    iv: valueIv,
    plaintext: new TextEncoder().encode(JSON.stringify(args.value)),
    aad: buildVaultItemAad(aadCtx),
  });

  // Name with metadata key
  const metaGcm = await importAesGcmKey(args.unlocked.metadataKey);
  const nameIv = generateIv();
  const nameAad = new TextEncoder().encode(`${args.item_id}:name`);
  const nameCt = await aesGcmEncrypt({
    key: metaGcm,
    iv: nameIv,
    plaintext: new TextEncoder().encode(args.name),
    aad: nameAad,
  });

  // Wrap CK for each recipient
  const wraps = await Promise.all(
    args.recipients.map(async (r) => {
      const wrapped = await wrapCkForRecipient(ck, r.ecdh_pub_jwk as EcdhPubJwk);
      return {
        recipient_id: r.user_id,
        ephemeral_pub_jwk: wrapped.ephemeralPubJwk,
        wrapped_ck_b64: toBase64(wrapped.wrappedCk),
      };
    }),
  );

  return {
    name_ct_b64: toBase64(nameCt),
    name_iv_b64: toBase64(nameIv),
    ciphertext_b64: toBase64(ciphertext),
    iv_b64: toBase64(valueIv),
    wraps,
  };
}

// =============================================================================
// Genesis SU bootstrap
// =============================================================================

export interface GenesisBootstrapArgs {
  password: string;
  email: string;
  personal_email: string;
  name: string;
  /**
   * Defaults to "SUPER_USER" for backward compatibility with the genesis
   * bootstrap flow. Employee/Admin invite acceptance MUST pass "EMPLOYEE"
   * or "ADMIN" so we skip Ed25519 signing-keypair generation + recovery
   * codes (those are SU-only per the security model). For non-SU roles
   * we send a zero-byte placeholder for pubkey_sig; the SU re-attests
   * the user's pubkey when wrapping a vault item for them.
   */
  role?: "SUPER_USER" | "ADMIN" | "EMPLOYEE";
}

export interface GenesisBootstrapResult {
  body: Record<string, unknown>;
  unlocked: UnlockedState;
  recoveryCodesPlain: string[];
}

export async function bootstrapGenesisLocal(args: GenesisBootstrapArgs): Promise<GenesisBootstrapResult> {
  const { aesKwWrap } = await import("@shared/crypto/aes.js");
  const { generateRecoveryCodes } = await import("@shared/crypto/recovery-codes.js");
  const { signRecipient } = await import("@shared/crypto/signing.js");
  const { generateKdfSalt } = await import("@shared/crypto/argon2.js");
  const { randomBytes, ulid } = await import("@shared/crypto/bytes.js");

  const now = Date.now();
  const userId = ulid(now);
  const kdfSalt = generateKdfSalt();
  const kdfParams = DEFAULT_ARGON2ID_PARAMS;

  const kek = await deriveKek({
    password: normalizePassword(args.password),
    salt: kdfSalt,
    params: kdfParams,
  });

  // ASK = random 32B; wrap under KEK via AES-KW; prepend 12-byte zero envelope marker
  const ask = generateAesKey();
  const kekKw = await importAesKwKey(kek);
  const askWrapped = await aesKwWrap(kekKw, ask);
  const wrappedAsk = new Uint8Array(12 + askWrapped.length);
  wrappedAsk.set(askWrapped, 12);

  // ECDH keypair; wrap priv under ASK via AES-GCM with random IV
  const ecdh = await generateEcdhKeypair();
  const askGcm = await importAesGcmKey(ask);
  const privBytes = new TextEncoder().encode(ecdh.privJwk);
  const wpIv = generateIv();
  const wpCt = await aesGcmEncrypt({ key: askGcm, iv: wpIv, plaintext: privBytes });
  const wrappedPriv = new Uint8Array(12 + wpCt.length);
  wrappedPriv.set(wpIv, 0);
  wrappedPriv.set(wpCt, 12);

  // Metadata key = random 32B; wrap under ASK via AES-GCM
  const metaRaw = randomBytes(32);
  const wmIv = generateIv();
  const wmCt = await aesGcmEncrypt({ key: askGcm, iv: wmIv, plaintext: metaRaw });
  const wrappedMeta = new Uint8Array(12 + wmCt.length);
  wrappedMeta.set(wmIv, 0);
  wrappedMeta.set(wmCt, 12);

  const role = args.role ?? "SUPER_USER";
  const isSuperUser = role === "SUPER_USER";

  // SU-only: generate Ed25519 signing keypair, wrap priv under ASK, self
  // attest pubkey. Non-SU users get a 64-byte zero placeholder for
  // pubkey_sig (Ed25519 signature length) and no wrapped signing priv.
  // The SU re-signs the user's recipient pubkey when wrapping any vault
  // item to them, so the placeholder is never load-bearing.
  let signingPubJwk: Awaited<ReturnType<typeof generateSigningKeypair>>["pubJwk"] | null = null;
  let wrappedSigningPrivB64: string | null = null;
  let pubkeySigBytes: Uint8Array = new Uint8Array(64); // placeholder zeros
  if (isSuperUser) {
    const signing = await generateSigningKeypair();
    const wsIv = generateIv();
    const wsCt = await aesGcmEncrypt({ key: askGcm, iv: wsIv, plaintext: signing.priv });
    const wrappedSigningPriv = new Uint8Array(12 + wsCt.length);
    wrappedSigningPriv.set(wsIv, 0);
    wrappedSigningPriv.set(wsCt, 12);
    const userIdBranded = userId as unknown as Parameters<typeof signRecipient>[1];
    pubkeySigBytes = await signRecipient(
      signing.priv,
      userIdBranded,
      ecdh.pubJwk,
      "SUPER_USER",
    );
    signingPubJwk = signing.pubJwk;
    wrappedSigningPrivB64 = toBase64(wrappedSigningPriv);
  }

  // Recovery codes are SU-only
  const recovery = isSuperUser ? await generateRecoveryCodes() : null;

  const mph = computeMasterPasswordHash({ kek, kdfSalt });

  const body: Record<string, unknown> = {
    email: args.email.toLowerCase(),
    personal_email: args.personal_email.toLowerCase(),
    name: args.name,
    kdf_algo: "argon2id",
    kdf_params_json: JSON.stringify(kdfParams),
    kdf_salt_b64: toBase64(kdfSalt),
    ecdh_pub_jwk: ecdh.pubJwk,
    wrapped_priv_b64: toBase64(wrappedPriv),
    wrapped_ask_b64: toBase64(wrappedAsk),
    wrapped_metadata_key_b64: toBase64(wrappedMeta),
    signing_pub_jwk: signingPubJwk,
    pubkey_sig_b64: toBase64(pubkeySigBytes),
    wrapped_signing_priv_b64: wrappedSigningPrivB64,
    master_password_hash_b64: toBase64(mph),
    ...(recovery
      ? { recovery_code_hashes_b64: recovery.codeHashes.map((h) => toBase64(h)) }
      : {}),
  };

  const unlocked: UnlockedState = {
    kek,
    ask,
    ecdhPrivJwk: ecdh.privJwk,
    ecdhPubJwk: ecdh.pubJwk,
    metadataKey: asBrand<RawSymmetricKey>(metaRaw),
    signingPubJwk,
  };

  return {
    body,
    unlocked,
    recoveryCodesPlain: recovery ? [...recovery.displayCodes] : [],
  };
}
